import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { API_URL } from './config';
import DashboardComponent from './DashboardComponent';

const ShiftClosingReport = () => {
    const navigate = useNavigate();
    const companyId = localStorage.getItem('company_id');
    const userId = localStorage.getItem('user_id');
    const branchId = localStorage.getItem('branch_id');
    const username = localStorage.getItem('userName');
    const [totalSales, setTotalSales] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [salesData, setSalesData] = useState([]);
    const [companyDetails, setCompanyDetails] = useState({});
    const [branchDetails, setBranchDetails] = useState({});
    const [inventoryMovement, setInventoryMovement] = useState([]);
    const [showInventory, setShowInventory] = useState(false);
    const [showPayments, setShowPayments] = useState(false);
    const [comment, setComment] = useState('');

    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                const companyResponse = await fetch(`${API_URL}/companysetup/${companyId}`);
                const companyData = await companyResponse.json();
                setCompanyDetails(companyData);
                console.log("Company Details:", companyData);

                const branchResponse = await fetch(`${API_URL}/branch/${branchId}`);
                const branchData = await branchResponse.json();
                setBranchDetails(branchData);
                console.log("Branch Details:", branchData);

                const salesResponseTotals = await fetch(`${API_URL}/salepostrans/total/sales/40/1/17`);
                const salesDataTotals = await salesResponseTotals.json();
                setTotalSales(salesDataTotals);
                console.log("Total Sales Data:", salesDataTotals);

                const inventoryResponse = await fetch(`${API_URL}/inventorymgt/inventory_movement/${branchId}/${companyId}/17`);
                const inventoryData = await inventoryResponse.json();
                setInventoryMovement(inventoryData);
                console.log("Inventory Movement Data:", inventoryData);

                const salesResponse = await fetch(`${API_URL}/salepostrans/get_sale_pos_trans_sold_by_shift/40/1/17`);
                if (!salesResponse.ok) throw new Error('Failed to fetch sales data');
                const salesTransData = await salesResponse.json();
                setSalesData(Array.isArray(salesTransData) ? salesTransData : [salesTransData]);

            } catch (error) {
                console.error('Error fetching data:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [companyId, branchId]);

    const handleCloseShift = async () => {
        if (!comment) {
            Swal.fire('Error', 'Please enter a comment before closing the shift.', 'error');
            return;
        }

        const shiftId = 17; 
        try {
            const fetchShiftResponse = await fetch(`${API_URL}/shift/${shiftId}`);
            const shiftData = await fetchShiftResponse.json();
            console.log("Fetched Shift Details:", shiftData);

            const updatedShift = {
                ...shiftData,
                company_id: companyId,
                branch_id: branchId,
                shift_status: "closed",
                date_time_close: new Date().toISOString(),
                shift_comment: comment,
                operator_id: userId,
            };

            console.log("Updated Shift Object:", updatedShift);

            const updateResponse = await fetch(`${API_URL}/shift/${shiftId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedShift),
            });

            if (updateResponse.ok) {
                Swal.fire('Success', 'Shift closed successfully.', 'success');
                localStorage.removeItem('company_id');
                localStorage.removeItem('branch_id');
                localStorage.removeItem('user_id');
                localStorage.removeItem('userName');
                navigate('/'); // Redirect after successful closure
            } else {
                throw new Error('Failed to update shift');
            }
        } catch (error) {
            console.error('Error closing shift:', error);
            Swal.fire('Error', 'Failed to close shift: ' + error.message, 'error');
        }
    };

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center min-vh-100">
            <div className="spinner-border text-secondary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

    return (
        <DashboardComponent>
            <div className="container-fluid py-4 bg-light">
                {/* Report Information */}
                <div className="card col-md-12 mb-4">
                    <div className="card-header bg-secondary text-white">
                        <h5 className="mb-0">Report Information</h5>
                    </div>
                    <div className="card-body">
                        <div className="col-md-6 column">
                            <div className="col-md-12 text-start" style={{marginBottom: 10}}>
                                <h6>Company Details</h6>
                                <p className="mb-1"><strong>Name:</strong> {companyDetails.name}</p>
                                <p className="mb-1"><strong>Email:</strong> {companyDetails.email1}</p>
                                <p className="mb-1"><strong>Phone:</strong> {companyDetails.phone_number1}</p>
                            </div>

                            <div className="col-md-12 text-start">
                                <h6 style={{marginTop: 20}}>Branch Details</h6>
                                <p className="mb-1"><strong>Name:</strong> {branchDetails.branch_name}</p>
                                <p className="mb-1"><strong>Location:</strong> {branchDetails.branch_location}</p>
                                <p className="mb-1"><strong>Operator:</strong> {username}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Cash Information */}
                <div className="d-flex justify-content-end">
                    <div className="card col-md-12 mb-4">
                        <div className="card-header bg-white">
                            <h5 className="mb-0 text-end">Cash Information</h5>
                        </div>
                        <div className="card-body">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Total Debit</th>
                                        <th>Total Credit</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {totalSales.map((sale, index) => (
                                        <tr key={index}>
                                            <td className="text-end">
                                                {sale.total_amntrec?.toFixed(2)} {sale.currency}
                                            </td>
                                            <td className="text-end">
                                                {sale.total_amntrec_credit?.toFixed(2)} {sale.currency}
                                            </td>
                                            <td className="text-end">
                                                {(sale.total_amntrec + sale.total_amntrec_credit)?.toFixed(2)} {sale.currency}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Inventory Movement */}
                <div className="card mb-4">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center" 
                         onClick={() => setShowInventory(!showInventory)} 
                         style={{cursor: 'pointer'}}>
                        <h5 className="mb-0">Inventory Movement</h5>
                        <span>{showInventory ? '▼' : '▶'}</span>
                    </div>
                    {showInventory && (
                        <div className="card-body">
                            {inventoryMovement.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>Product ID</th>
                                                <th>Code</th>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Quantity</th>
                                                <th>Cost</th>
                                                <th>Selling Price</th>
                                                <th>Gross Profit</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {inventoryMovement.map((item, index) => {
                                                const grossProfit = (item.selling_price - item.cost) * item.quantity;
                                                return (
                                                    <tr key={index}>
                                                        <td>{item.product_id}</td>
                                                        <td>{item.product_code}</td>
                                                        <td>{item.product_name}</td>
                                                        <td>{item.description}</td>
                                                        <td>{item.quantity}</td>
                                                        <td>{item.total_unit_cost}</td>
                                                        <td>{item.total_selling_price}</td>
                                                        <td>{item.total_selling_price - item.total_unit_cost}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="alert alert-info">No inventory movement data available</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Payments Information */}
                <div className="card mb-4">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center"
                         onClick={() => setShowPayments(!showPayments)}
                         style={{cursor: 'pointer'}}>
                        <h5 className="mb-0">Payments Information</h5>
                        <span>{showPayments ? '▼' : '▶'}</span>
                    </div>
                    {showPayments && (
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Product ID</th>
                                            <th>Code</th>
                                            <th>Name</th>
                                            <th>Description</th>
                                            <th>Quantity</th>
                                            <th>Amount</th>
                                            <th>Currency</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {salesData.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.product_id}</td>
                                                <td>{item.product_code}</td>
                                                <td>{item.product_name}</td>
                                                <td>{item.description}</td>
                                                <td>{item.quantity}</td>
                                                <td>{item.amntrec}</td>
                                                <td>{item.currency}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Report Comments */}
                <div className="card mb-4">
                    <div className="card-header bg-white">
                        <h5 className="mb-0">Report Comments</h5>
                    </div>
                    <div className="card-body">
                        <textarea 
                            className="form-control mb-3" 
                            rows="3" 
                            placeholder="Enter report comments..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        ></textarea>
                        <div className="text-end">
                            <button 
                                className="btn btn-secondary" 
                                onClick={handleCloseShift}
                            >
                                Close Shift
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardComponent>
    );
};

export default ShiftClosingReport;